/* eslint-disable react-refresh/only-export-components -- Context files intentionally export both a context object and a hook from the same module. Splitting would break the encapsulation of this pattern. */
import { createContext, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/utils/cn';
import { Toast } from './Toast';
import type { ToastVariant } from './Toast';

// ─── Public types ─────────────────────────────────────────────────────────────

export type { ToastVariant };

export interface ShowToastOptions {
  /** Visual style and semantic meaning of the toast. */
  variant: ToastVariant;
  /** The message to display. */
  message: string;
  /**
   * How long (in milliseconds) the toast stays visible before auto-dismissing.
   * Defaults to 5000ms. Hovering over the toast pauses the countdown.
   */
  duration?: number;
  /**
   * When true, renders a close button on the toast.
   * Defaults to false.
   */
  showDismiss?: boolean;
}

export interface ToastContextValue {
  showToast: (options: ShowToastOptions) => void;
}

// ─── Internal types ───────────────────────────────────────────────────────────

interface ToastItem extends Required<ShowToastOptions> {
  id: string;
}

// ─── Context ──────────────────────────────────────────────────────────────────

export const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// ─── Animation variants ───────────────────────────────────────────────────────

/**
 * Toasts slide down from above and fade in on enter; reverse on exit.
 * `y: -16` starts the toast above its resting position — directionally
 * consistent with the top-of-screen anchor.
 */
const toastVariants = {
  hidden: { opacity: 0, y: -16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' as const },
  },
  exit: {
    opacity: 0,
    y: -16,
    transition: { duration: 0.15, ease: 'easeIn' as const },
  },
};

// ─── ToastItem — internal component ──────────────────────────────────────────

/**
 * Internal wrapper rendered per toast item.
 *
 * Owns the auto-dismiss timer for its toast. The timer:
 * - Starts immediately on mount.
 * - Pauses on `mouseenter` (hover) — prevents dismissal while the user is reading.
 * - Resumes on `mouseleave` with the full original duration (not remaining time).
 *   This keeps the implementation simple; the toast stays as long as needed after
 *   the user moves their cursor away.
 * - Clears on unmount to avoid calling setState on an unmounted component.
 */
interface ToastItemProps {
  item: ToastItem;
  onDismiss: (id: string) => void;
}

function ToastItemWrapper({ item, onDismiss }: ToastItemProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    onDismiss(item.id);
  }, [item.id, onDismiss]);

  const startTimer = useCallback(() => {
    timerRef.current = setTimeout(dismiss, item.duration);
  }, [dismiss, item.duration]);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Start auto-dismiss timer on mount; clear on unmount.
  useEffect(() => {
    startTimer();
    return clearTimer;
  }, [startTimer, clearTimer]);

  return (
    <motion.div
      key={item.id}
      layout="position"
      variants={toastVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onMouseEnter={clearTimer}
      onMouseLeave={startTimer}
      className="pointer-events-auto"
    >
      <Toast
        variant={item.variant}
        message={item.message}
        showDismiss={item.showDismiss}
        onDismiss={dismiss}
      />
    </motion.div>
  );
}

// ─── ToastProvider ────────────────────────────────────────────────────────────

interface ToastProviderProps {
  children: ReactNode;
}

/**
 * Primer ToastProvider.
 *
 * Wrap the application root with `<ToastProvider>` once. Consuming components
 * call `useToast()` to get `showToast()` and trigger toasts imperatively from
 * anywhere in the tree.
 *
 * Toasts are rendered into a React Portal attached to `#toast-root` (falling
 * back to `document.body`) so they always sit above all other content
 * regardless of stacking context.
 *
 * **Positioning:**
 * - Mobile (< 800px): full width minus 16px margins, anchored bottom.
 * - Tablet/desktop (≥ 800px): fixed 384px (w-96) anchored bottom-right.
 *
 * **Stacking:** new toasts appear on top of older ones via `flex-col-reverse`.
 *
 * **Hover-pause:** mousing over a toast pauses its auto-dismiss countdown;
 * moving away restarts the full timer. This prevents toasts dismissing while
 * the user is reading.
 *
 * @example
 * // App root
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 *
 * @example
 * // In any component
 * const { showToast } = useToast();
 * showToast({ variant: 'success', message: 'Saved!', showDismiss: true });
 * showToast({ variant: 'error',   message: 'Failed.', duration: 8000 });
 */
export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback((options: ShowToastOptions) => {
    const item: ToastItem = {
      id: crypto.randomUUID(),
      variant: options.variant,
      message: options.message,
      duration: options.duration ?? 5000,
      showDismiss: options.showDismiss ?? false,
    };
    setToasts((prev) => [...prev, item]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const contextValue: ToastContextValue = { showToast };

  // Portal target — `#toast-root` must exist in the app's HTML shell.
  // Falls back to document.body so the component works without it.
  const portalTarget =
    typeof document !== 'undefined'
      ? (document.getElementById('toast-root') ?? document.body)
      : null;

  const container = (
    <div
      className={cn(
        'fixed top-4 z-[9999]',
        'flex flex-col gap-2',
        'pointer-events-none',
        // Mobile: calc-based width fills viewport minus 32px (16px each side),
        // positioned 16px from the left. Avoids setting both left+right on a
        // fixed element, which causes the browser to derive width from the gap
        // between them and ignore an explicit w-* class.
        'left-4 w-[calc(100vw-2rem)]',
        // Tablet+: drop the calc width, pin right edge, fixed toast width.
        // left-auto removes the left-4 anchor so only right+width control position.
        // w-(--toast-width) references the --toast-width token defined in
        // breakpoints.css (24rem / 384px) — avoids a hardcoded arbitrary value.
        'md:left-auto md:right-4 md:w-(--toast-width)'
      )}
      // Accessible region — screen readers announce new toasts as they appear
      aria-label="Notifications"
      aria-live="polite"
      aria-relevant="additions"
    >
      <AnimatePresence initial={false}>
        {[...toasts].reverse().map((item) => (
          <ToastItemWrapper key={item.id} item={item} onDismiss={dismiss} />
        ))}
      </AnimatePresence>
    </div>
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {portalTarget !== null && createPortal(container, portalTarget)}
    </ToastContext.Provider>
  );
}
